/**
 * Custom cursors for Scale mode resize handles.
 *
 * Replaces tldraw's default `ns-resize` / `ew-resize` / `nwse-resize` /
 * `nesw-resize` glyphs with our scale-arrow SVG (white-haloed black
 * double-headed arrow). All four directions are produced by rotating
 * the same vertical (NS) SVG — 0°, 90°, 45° (nwse), 135° (nesw).
 *
 * ---
 * Why both a CSS-var override AND a JS subscription are needed
 * ---
 * Tldraw applies its cursor in two ways:
 *
 *   1. STATIC cursors (default, pointer, text, ...): tldraw sets
 *      `--tl-cursor: var(--tl-cursor-${type})` on its container, so
 *      redefining the relevant CSS variable under
 *      `body.is-scale-mode-active` swaps the glyph.
 *
 *   2. RESIZE / ROTATE cursors: tldraw generates a rotated SVG on
 *      the fly (see `useCursor.ts` inside `@tldraw/editor`) and sets
 *      `--tl-cursor` to that data URL DIRECTLY. The intermediate
 *      `--tl-cursor-${type}` vars are not read — so the CSS-var
 *      override approach does NOT work for resize cursors.
 *
 * The solution is to subscribe to tldraw's cursor state from React
 * and, while scale mode is active, write our own custom value to
 * `--tl-cursor` on the container. That covers hover AND drag (the
 * drag was previously handled by `useResizeCursor.ts` writing an
 * inline `cursor:` style; we now collapse both into this hook).
 */

import { useEffect, useState } from "react";
// `useQuickReactor` and `useContainer` are re-exported by `tldraw`
// (which the `@tldraw/tldraw` shim re-exports as well). We hit the
// underlying `@tldraw/editor` path here because the TS types resolve
// most cleanly that way given the local tsconfig.
import { useEditor, useQuickReactor, useContainer } from "@tldraw/editor";

// Paths from the Figma spec (vertical / NS double-headed arrow,
// updated to the slimmer revision: 0…268 × 0…603 bounding box,
// 20px stroke). Centered on (134, 301.5). The white halo paths are
// duplicates of the black ones drawn underneath with a wider stroke
// so the cursor stays legible on light AND dark canvases.
//
// We translate the paths so they're centered on (0, 0) inside the
// SVG, then apply `rotate(${deg})` for the four directions. Doing
// the rotation in SVG (rather than CSS) is essential because CSS
// `transform` does not apply to elements used as `cursor: url(...)`.
const PATHS_HALO = `
  <path d='M10 473L127.213 590.219C129.02 592.001 131.459 593 134 593C136.541 593 138.979 592.001 140.788 590.219L258 473.261' stroke='white' stroke-width='70' stroke-linecap='round' stroke-linejoin='round' fill='none'/>
  <path d='M258 130L140.787 12.7807C138.98 10.9991 136.541 10 134 10C131.459 10 129.021 10.9991 127.212 12.7807L10 129.739' stroke='white' stroke-width='70' stroke-linecap='round' stroke-linejoin='round' fill='none'/>
  <path d='M126.058 529.924C130.168 534.025 136.832 534.025 140.942 529.924L207.917 463.112C212.027 459.01 212.027 452.363 207.917 448.262C203.808 444.164 197.144 444.164 193.034 448.262L133.5 507.653L73.9659 448.262C69.856 444.164 63.1924 444.164 59.0825 448.262C54.9725 452.363 54.9725 459.01 59.0825 463.112L126.058 529.924ZM140.942 73.0751C136.832 68.975 130.168 68.975 126.058 73.0751L59.0825 139.889C54.9725 143.989 54.9725 150.637 59.0825 154.737C63.1925 158.837 69.856 158.837 73.9659 154.737L133.5 95.3466L193.034 154.737C197.144 158.837 203.808 158.837 207.917 154.737C212.027 150.637 212.027 143.989 207.917 139.889L140.942 73.0751ZM133.5 522.503H144.024V80.4988H133.5H122.976V522.503H133.5Z' stroke='white' stroke-width='50' stroke-linecap='round' stroke-linejoin='round' fill='white'/>
`;
const PATHS_FILL = `
  <path d='M10 473L127.213 590.219C129.02 592.001 131.459 593 134 593C136.541 593 138.979 592.001 140.788 590.219L258 473.261' stroke='black' stroke-width='20' stroke-linecap='round' stroke-linejoin='round' fill='none'/>
  <path d='M258 130L140.787 12.7807C138.98 10.9991 136.541 10 134 10C131.459 10 129.021 10.9991 127.212 12.7807L10 129.739' stroke='black' stroke-width='20' stroke-linecap='round' stroke-linejoin='round' fill='none'/>
  <path d='M126.058 529.924C130.168 534.025 136.832 534.025 140.942 529.924L207.917 463.112C212.027 459.01 212.027 452.363 207.917 448.262C203.808 444.164 197.144 444.164 193.034 448.262L133.5 507.653L73.9659 448.262C69.856 444.164 63.1924 444.164 59.0825 448.262C54.9725 452.363 54.9725 459.01 59.0825 463.112L126.058 529.924ZM140.942 73.0751C136.832 68.975 130.168 68.975 126.058 73.0751L59.0825 139.889C54.9725 143.989 54.9725 150.637 59.0825 154.737C63.1925 158.837 69.856 158.837 73.9659 154.737L133.5 95.3466L193.034 154.737C197.144 158.837 203.808 158.837 207.917 154.737C212.027 150.637 212.027 143.989 207.917 139.889L140.942 73.0751ZM133.5 522.503H144.024V80.4988H133.5H122.976V522.503H133.5Z' fill='black'/>
`;

// Square viewBox big enough that the rotated arrow fits at any
// angle (diagonal of the 268×603 bounding box is ~660; adding white
// halo margin pushes the safe extent to ~720). 800 leaves slack.
// Center at (0,0) so a single `rotate(<deg>)` transform suffices.
const VIEW = 800;
const HALF = VIEW / 2;
// Final on-screen size — 28×28 square. A hair larger than the OS
// standard 24×24 so the slim 20px-stroke artwork stays readable
// without dwarfing the resize handle it's sitting on. The square
// viewBox keeps the rotated diagonal variants visually balanced.
const CURSOR_PX = 28;
const HOTSPOT = CURSOR_PX / 2; // center

function buildScaleCursor(rotationDegrees: number): string {
  // Drop newlines so the data URL is a single line — CSS `url(...)`
  // tolerates whitespace, but inline-style assignment in some
  // browsers gets fussy with multi-line strings.
  const halo = PATHS_HALO.replace(/\s+/g, " ").trim();
  const fill = PATHS_FILL.replace(/\s+/g, " ").trim();
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${CURSOR_PX}' height='${CURSOR_PX}' ` +
    `viewBox='-${HALF} -${HALF} ${VIEW} ${VIEW}'>` +
    // Center of the source paths is (134, 301.5) inside the original
    // 268×603 bounding box; translating by the negative of that puts
    // the artwork on the SVG origin so `rotate(${deg})` spins around
    // the visual midpoint.
    `<g transform='rotate(${rotationDegrees}) translate(-134 -301.5)'>` +
    halo +
    fill +
    `</g></svg>`;
  // Fallback to the matching standard cursor in case the SVG fails
  // to load (e.g. if the data URL exceeds a browser limit).
  return `url("data:image/svg+xml;utf8,${svg}") ${HOTSPOT} ${HOTSPOT}, ns-resize`;
}

// One entry per tldraw cursor type. NS is the source orientation
// (0°); the others are rotations of the same artwork.
//
// SVG rotation is clockwise (Y axis points down). Starting from a
// vertical (NS) arrow, rotating clockwise by:
//   45°  puts the arrowheads at NE / SW → this is `nesw-resize`
//   135° puts the arrowheads at SE / NW → this is `nwse-resize`
//
// (Previous version had these two swapped — visible as diagonal
// cursors pointing along the wrong diagonal.)
export const SCALE_CURSOR_BY_TYPE: Record<string, string> = {
  "ns-resize": buildScaleCursor(0),
  "ew-resize": buildScaleCursor(90),
  "nesw-resize": buildScaleCursor(45),
  "nwse-resize": buildScaleCursor(135),
};

// CSS overrides for the hover case.
//
// Tldraw renders each resize handle as a transparent `<rect>` inside
// `TldrawSelectionForeground` with `style={{ cursor: getCursor(...) }}`
// applied INLINE (see `node_modules/tldraw/.../TldrawSelectionForeground.js`).
// The inline style is what changes the cursor when you hover an edge
// or corner — the canvas-wide `editor.cursor.type` stays at "default"
// the entire time (confirmed via console.log).
//
// CSS `!important` beats inline styles that lack `!important`, so we
// target each handle by its `data-testid` and override the cursor
// with our custom glyph while `body.is-scale-mode-active`. The
// `data-testid` attributes are emitted unconditionally by tldraw, so
// they're stable across dev / prod.
//
// The CSS-var overrides further down still apply for any path that
// reads `--tl-cursor-${type}` (currently a defensive belt — most
// of tldraw's cursor logic generates URLs directly).
const STYLE_ID = "scale-mode-cursor-overrides";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const styleEl = document.createElement("style");
  styleEl.id = STYLE_ID;
  styleEl.textContent = `
    body.is-scale-mode-active [data-testid="selection.resize.top"],
    body.is-scale-mode-active [data-testid="selection.resize.bottom"] {
      cursor: ${SCALE_CURSOR_BY_TYPE["ns-resize"]} !important;
    }
    body.is-scale-mode-active [data-testid="selection.resize.left"],
    body.is-scale-mode-active [data-testid="selection.resize.right"] {
      cursor: ${SCALE_CURSOR_BY_TYPE["ew-resize"]} !important;
    }
    body.is-scale-mode-active [data-testid="selection.target.top-left"],
    body.is-scale-mode-active [data-testid="selection.target.bottom-right"] {
      cursor: ${SCALE_CURSOR_BY_TYPE["nwse-resize"]} !important;
    }
    body.is-scale-mode-active [data-testid="selection.target.top-right"],
    body.is-scale-mode-active [data-testid="selection.target.bottom-left"] {
      cursor: ${SCALE_CURSOR_BY_TYPE["nesw-resize"]} !important;
    }
    body.is-scale-mode-active {
      --tl-cursor-ns-resize: ${SCALE_CURSOR_BY_TYPE["ns-resize"]};
      --tl-cursor-ew-resize: ${SCALE_CURSOR_BY_TYPE["ew-resize"]};
      --tl-cursor-nesw-resize: ${SCALE_CURSOR_BY_TYPE["nesw-resize"]};
      --tl-cursor-nwse-resize: ${SCALE_CURSOR_BY_TYPE["nwse-resize"]};
    }
  `;
  document.head.appendChild(styleEl);
}

/**
 * Module-level signal for "scale mode is on". Maintained alongside
 * the body class so React subscribers (`useScaleModeActive`) get
 * notified the moment scale flips — we can't rely on shape props
 * (`isScale: true`) because those only update on the shapes that
 * were selected when scale was toggled; selecting a different shape
 * while scale is on leaves the new shape's `isScale` false and
 * would otherwise turn our cursor off mid-session.
 */
let scaleModeActive = false;
const scaleModeListeners = new Set<(active: boolean) => void>();

/**
 * Toggle scale mode globally:
 *   - Adds/removes the `is-scale-mode-active` body class (gates the
 *     CSS-var overrides for tldraw's static cursor variants).
 *   - Updates the module-level `scaleModeActive` flag and notifies
 *     React listeners (gates the JS override applied in
 *     `useScaleResizeCursor` below).
 * No-ops when state hasn't actually changed.
 */
export const setScaleModeCursorActive = (active: boolean): void => {
  if (typeof document === "undefined") return;
  if (scaleModeActive === active) return;
  scaleModeActive = active;
  document.body.classList.toggle("is-scale-mode-active", active);
  scaleModeListeners.forEach((listener) => listener(active));
};

/** React hook that re-renders when scale mode toggles. */
const useScaleModeActive = (): boolean => {
  const [active, setActive] = useState(scaleModeActive);
  useEffect(() => {
    scaleModeListeners.add(setActive);
    // Sync once on mount in case the value already flipped before
    // this hook was wired up.
    setActive(scaleModeActive);
    return () => {
      scaleModeListeners.delete(setActive);
    };
  }, []);
  return active;
};

// Backwards-compat export — `useResizeCursor.ts` previously imported
// `SCALE_CURSOR_NS` to override the inline cursor during active drag.
// The new React hook below handles that case via `--tl-cursor`, but
// we keep the export so the old import path still resolves until the
// next refactor.
export const SCALE_CURSOR_NS = SCALE_CURSOR_BY_TYPE["ns-resize"];

const SCALE_RESIZE_CURSOR_TYPES = new Set(["ns-resize", "ew-resize", "nwse-resize", "nesw-resize"]);

/**
 * Hook that watches tldraw's cursor state and, while scale mode is
 * active, overrides `--tl-cursor` on the tldraw container so the
 * custom scale-arrow glyph shows even when tldraw is generating its
 * own rotated cursor SVG (the hover-on-handle case). When the
 * cursor type is not a resize variant, we step out of the way so
 * tldraw's normal logic wins.
 *
 * Mount once at the top of the editor (same place as
 * `ResizeCursorManager`).
 */
export const useScaleResizeCursor = (): void => {
  const editor = useEditor();
  // Same container that tldraw's own `useCursor` writes to, so our
  // override lives on the exact same element. Querying the DOM would
  // be brittle in multi-editor scenarios; the context guarantees the
  // right node.
  const container = useContainer();
  // Scale-mode state comes from our module-level signal so it tracks
  // the toggle action (BottomToolPanel / ScaleTool calls), NOT the
  // `isScale` flag on shape props — that flag only updates on the
  // shapes selected at toggle time and would miss later selections.
  const isScaleMode = useScaleModeActive();

  // `useQuickReactor` runs SYNCHRONOUSLY whenever the reactive deps
  // change, in the same task as tldraw's own `useCursor`. By doing
  // our override in the same execution phase we avoid the React-
  // useEffect-fires-after-the-reactor race that left the hover case
  // showing tldraw's value instead of ours.
  useQuickReactor(
    "useScaleResizeCursor",
    () => {
      if (!container || !isScaleMode) return;
      const cursorType = editor.getInstanceState().cursor?.type;
      if (cursorType && SCALE_RESIZE_CURSOR_TYPES.has(cursorType)) {
        container.style.setProperty("--tl-cursor", SCALE_CURSOR_BY_TYPE[cursorType]);
      }
    },
    [editor, container, isScaleMode]
  );

  // When scale mode flips OFF, clear any override left behind so
  // tldraw's normal cursor logic owns the container again. (The
  // reactor above only writes; it doesn't clean up because there's
  // no symmetric "stop" condition inside a useQuickReactor body.)
  useEffect(() => {
    if (!container) return;
    if (!isScaleMode) {
      container.style.removeProperty("--tl-cursor");
    }
  }, [container, isScaleMode]);
};

/**
 * Renders nothing; runs `useScaleResizeCursor` in the editor tree.
 * Mounted next to `ResizeCursorManager` inside the `Tldraw` root so
 * it has access to the editor context.
 */
export function ScaleResizeCursorManager(): null {
  useScaleResizeCursor();
  return null;
}
